/**
 * components/Modal
 * ──────────────────────────────────────────────────────────────────────────
 * The one modal shell. Thirteen dialogs in this product use it; none of them
 * declares a shell of its own.
 *
 * Geometry from the design: centred over `rgba(8,9,10,0.42)`, 520px wide,
 * `max-height: 88vh` with a scrollable body, 10px radius. Header carries the
 * title and a one-line hint, the body pads 18px with 13px between fields, and
 * the footer is #FAFAFA with Cancel and the primary right-aligned.
 *
 * CLOSING ON THE BACKDROP — the detail that makes this feel finished.
 * The modal closes only when the press AND the release both land on the
 * backdrop. Without tracking the press, this happens: a user opens a native
 * picker inside the modal, clicks away to dismiss the picker, that click lands
 * on the backdrop, and the whole modal closes. It reads as the app throwing
 * away their work. Tracking the press start fixes it in one flag.
 *
 * On a narrow viewport the modal becomes a bottom sheet — full width, rounded
 * at the top only. A 520px centred card on a 360px phone is a card with 20px
 * of margin and nowhere to put a keyboard.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useRef, type ReactNode } from 'react';
import {
  Modal as RNModal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { neutral, scrim } from '../../theme/colors';
import { radii } from '../../theme/radii';
import { chrome } from '../../theme/spacing';
import { modalShadow } from '../../theme/shadows';
import { sans, size as fontSize, leading } from '../../theme/typography';
import { useResponsive } from '../../lib/responsive';
import Button from '../Button';
import IconButton from '../IconButton';

export interface ModalProps {
  visible: boolean;
  title: string;
  /** The one-line hint under the title. */
  hint?: string;
  onClose: () => void;
  children: ReactNode;
  /** The confirming action. Omit for a modal that only informs. */
  primaryLabel?: string;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  primaryLoading?: boolean;
  cancelLabel?: string;
  /** Extra controls on the left of the footer — "Load a sample org", say. */
  footerLeft?: ReactNode;
}

export function Modal({
  visible,
  title,
  hint,
  onClose,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryLoading = false,
  cancelLabel = 'Cancel',
  footerLeft,
}: ModalProps) {
  const { height, isMobile } = useResponsive();

  // See the header comment: a release on the backdrop only closes the modal if
  // the press started there too.
  const pressStartedOnBackdrop = useRef(false);

  const onBackdropPressIn = useCallback(() => {
    pressStartedOnBackdrop.current = true;
  }, []);

  const onBackdropPress = useCallback(() => {
    if (pressStartedOnBackdrop.current) onClose();
    pressStartedOnBackdrop.current = false;
  }, [onClose]);

  return (
    <RNModal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      // Android's hardware back button maps to onRequestClose, which is the
      // platform-correct "dismiss" and matches Escape on the web.
      statusBarTranslucent
    >
      <Pressable
        style={styles.backdrop}
        onPressIn={onBackdropPressIn}
        onPress={onBackdropPress}
        accessibilityLabel="Close"
      >
        {/*
          The card stops the press from reaching the backdrop. `onPressIn` here
          clears the flag as well, so a drag that starts inside the card and
          ends outside it does not close the modal either.
        */}
        <Pressable
          onPressIn={() => {
            pressStartedOnBackdrop.current = false;
          }}
          style={[
            styles.card,
            isMobile ? styles.sheet : styles.centred,
            { maxHeight: height * chrome.modalMaxHeightRatio },
            modalShadow,
          ]}
        >
          <View style={styles.header}>
            <View style={styles.headerText}>
              <Text style={styles.title}>{title}</Text>
              {!!hint && <Text style={styles.hint}>{hint}</Text>}
            </View>
            <IconButton
              icon="close"
              onPress={onClose}
              accessibilityLabel="Close"
              bordered={false}
            />
          </View>

          <ScrollView
            style={styles.bodyScroll}
            contentContainerStyle={styles.body}
            keyboardShouldPersistTaps="handled"
          >
            {children}
          </ScrollView>

          {(!!primaryLabel || !!footerLeft) && (
            <View style={styles.footer}>
              <View style={styles.footerLeft}>{footerLeft}</View>
              <View style={styles.footerRight}>
                <Button label={cancelLabel} size="modal" variant="outline" onPress={onClose} />
                {!!primaryLabel && (
                  <Button
                    label={primaryLabel}
                    size="modal"
                    variant="primary"
                    onPress={onPrimary}
                    disabled={primaryDisabled}
                    loading={primaryLoading}
                  />
                )}
              </View>
            </View>
          )}
        </Pressable>
      </Pressable>
    </RNModal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: scrim.modal,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  card: {
    width: '100%',
    backgroundColor: neutral.surface,
    overflow: 'hidden',
  },
  centred: { maxWidth: chrome.modalWidth, borderRadius: radii.modal },
  // Bottom sheet on a phone: square at the bottom, flush to the edges.
  sheet: {
    maxWidth: '100%',
    marginTop: 'auto',
    marginBottom: -16,
    marginHorizontal: -16,
    borderTopLeftRadius: radii.modal,
    borderTopRightRadius: radii.modal,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    // 16px / 18px, from the design source.
    paddingVertical: 16,
    paddingHorizontal: 18,
    borderBottomWidth: 1,
    borderBottomColor: neutral.border,
  },
  headerText: { flex: 1, minWidth: 0, gap: 3 },
  // 15px / 600 — a modal title is one step up from a card title.
  title: { ...sans('600'), fontSize: fontSize.md, color: neutral.ink },
  hint: {
    ...sans('400'),
    fontSize: fontSize.label,
    lineHeight: leading(fontSize.label),
    color: neutral.grey600,
  },
  bodyScroll: { flexGrow: 0 },
  body: { padding: 18, gap: 15 },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    paddingVertical: 12,
    paddingHorizontal: 18,
    backgroundColor: neutral.surface2,
    borderTopWidth: 1,
    borderTopColor: neutral.border,
  },
  footerLeft: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 1 },
  footerRight: { flexDirection: 'row', alignItems: 'center', gap: 8, flexShrink: 0 },
});

export default Modal;
